import { Card, CardBody } from '@statsbomb/kitbag-components';
import { MetricValueMode } from '@statsbomb/parachute-types';
import { useAtomValue, useSetAtom } from 'jotai';
import { ScopeProvider } from 'jotai-scope';
import { useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { playerDataDefaultColumnsAtom } from '@/atoms/dataLocker/player/table';
import { resetFiltersToLastAppliedFiltersAtom } from '@/atoms/filters/resetFilters';
import { playerLinkAtom, playerPreviewLink } from '@/atoms/navigation';
import { playerAggsBaseFiltersAtom, playerAggsShowApplyButtonAtom } from '@/atoms/queries/player/playerAggData';
import { metricValueModeAtom, tableSelectedRowIdsAtom } from '@/atoms/table';
import { TableDataControls } from '@/components/controls/TableDataControls';
import { PlayerAggsFilters } from '@/components/dataLocker/PlayerAggsFilters';
import { UserFilters } from '@/components/filters/UserFilters/UserFilters';
import { PageTitle } from '@/components/head/PageTitle';
import { LayoutWithFilters } from '@/components/layout/LayoutWithFilters';
import { FirstTimeModalAiSummary } from '@/components/modal/FirstTimeModals/FirstTimeModalAiSummary';
import { OnboardingSettingsButton } from '@/components/onboarding/OnboardingSettingsButton';
import { AppearanceSettingsPanel } from '@/components/settings/AppearanceSettingsPanel';
import { PlayerAggsTableWithPagination } from '@/components/tables/PlayerAggsTable';
import { ScopedTableWithDefaultColumns } from '@/components/tables/ScopedTableWithDefaultColumns';
import { dataLockerPageScopeAtoms } from '@/consts/scope';
import { useCsvDownload } from '@/hooks/exporting/useCsvDownload';
import { useInitialisePlayerAggsFilters } from '@/hooks/useInitialisePlayerAggsFilters';
import { useSelectedFilterSetName } from '@/hooks/userFilters/useSelectedFilterSetName';
import { useStatus } from '@/hooks/useStatus';
import { playersNormalisedAggregatesUrl, playersTotalAggregatesUrl } from '@/query/url';
import { StatusTranslationKey } from '@/types/status';
import { TableId } from '@/types/table';
import { OnboardingItemKey } from '@/types/userConfigs';
import { ThreeSixtyMissingDataInfoBar } from '../../ThreeSixtyMissingDataInfoBar';
import { EntityDataLockerContainer } from '../EntityDataLockerContainer';
import type { FilterParams } from '@statsbomb/parachute-types';

const getPlayerUrl = (playerAggsBaseFilters: FilterParams | null, metricValueMode: MetricValueMode) => {
  if (!playerAggsBaseFilters) return undefined;

  if (metricValueMode === MetricValueMode.Per90) {
    return playersNormalisedAggregatesUrl(playerAggsBaseFilters);
  }

  return playersTotalAggregatesUrl(playerAggsBaseFilters);
};

const CsvDownload = ({ pageTitle }: { pageTitle: string }) => {
  const playerAggsBaseFilters = useAtomValue(playerAggsBaseFiltersAtom);
  const metricValueMode = useAtomValue(metricValueModeAtom);

  const url = getPlayerUrl(playerAggsBaseFilters, metricValueMode);

  useCsvDownload(pageTitle, url, { includeOnDemandMetrics: true, appliedFilters: playerAggsBaseFilters });
  return null;
};

const SetLinkBehaviour = () => {
  const setPlayerLink = useSetAtom(playerLinkAtom);
  // useLayoutEffect (not useEffect) so the scoped link is set before paint — otherwise a classic-mode
  // user can see/click the classic dashboard anchor on the first render before the preview link applies.
  useLayoutEffect(() => {
    setPlayerLink(playerPreviewLink);
  }, []);
  return null;
};

const TableWithControls = () => {
  const { t } = useTranslation(['navigation']);

  const filtersApplicationRequired = useAtomValue(playerAggsShowApplyButtonAtom);
  const filtersStatusProps = useStatus('filters', StatusTranslationKey.INFO);

  return (
    <ScopeProvider atoms={[tableSelectedRowIdsAtom]}>
      <Card>
        <TableDataControls heading={t(`crossPlatform.secondary.playerScout`)}>
          <OnboardingSettingsButton />
        </TableDataControls>
        <ThreeSixtyMissingDataInfoBar translationKey="hiddenPlayersInfoBar" />
        <CardBody {...(filtersApplicationRequired ? filtersStatusProps : {})}>
          <PlayerAggsTableWithPagination />
        </CardBody>
      </Card>
    </ScopeProvider>
  );
};

const TableWithControlsWrapper = ({ pageTitle }: { pageTitle: string }) => (
  <EntityDataLockerContainer>
    <CsvDownload pageTitle={pageTitle} />
    <SetLinkBehaviour />
    <TableWithControls />
  </EntityDataLockerContainer>
);

const PlayerFilterParams = () => {
  useInitialisePlayerAggsFilters();
  return null;
};

export const PlayerDataLockerPage = () => {
  const { t } = useTranslation('navigation');
  const pageTitle = t('crossPlatform.secondary.playerScout');

  const resetFiltersToLastAppliedFilters = useSetAtom(resetFiltersToLastAppliedFiltersAtom);
  const filtersTitle = useSelectedFilterSetName();

  return (
    <>
      <PlayerFilterParams />
      <ScopeProvider atoms={[playerLinkAtom, ...dataLockerPageScopeAtoms]}>
        <PageTitle>{pageTitle}</PageTitle>
        {/* NOTE - LayoutWithFilters needs to be inside ScopedTableWithDefaultColumns as it has a dependency on the order by field and order direction. */}
        <ScopedTableWithDefaultColumns defaultColumnsAtom={playerDataDefaultColumnsAtom} tableId={TableId.PLAYER_AGGS}>
          {/* metricValueModeAtom is scoped at the DataLockerLayout level via layoutScopeAtoms, which means all data locker
              pages share the same instance. This page-level scope isolates it so changes here don't leak to other pages. */}
          <LayoutWithFilters
            onClickMobileClose={resetFiltersToLastAppliedFilters}
            filters={<PlayerAggsFilters />}
            filtersTitle={filtersTitle}
            filtersActions={<UserFilters />}
            settingsPanel={<AppearanceSettingsPanel />}
          >
            <TableWithControlsWrapper pageTitle={pageTitle} />
          </LayoutWithFilters>
        </ScopedTableWithDefaultColumns>
      </ScopeProvider>
      <FirstTimeModalAiSummary modalType={OnboardingItemKey.AI_SUMMARY_SCOUT} />
    </>
  );
};
